import { type MapTheme } from './theme';
import { type CityPlan, type PlacedBuilding, type Point, type RoadSegment } from './cityPlan';
import { spriteImage } from './sprites';
import { bakeBoardLayer } from './boardArt';
import { bakeDioramaLayer } from './diorama';
import { currentLook } from './look';

export interface BackgroundProjection {
  toCanvas(position: { x: number; y: number }): { x: number; y: number };
  unitScale: number;
}

interface CityPalette {
  readonly ground: string;
  readonly road: string;
  readonly roadCasing: string;
  readonly alley: string;
  readonly plaza: string;
  readonly water: string;
  readonly waterSheen: string;
  readonly wall: string;
  readonly shadow: string;
  readonly buildingFill: string;
  readonly buildingRoof: string;
  readonly buildingStroke: string;
}

const BUILDING_ALPHA = 0.93;
const SHADOW_ALPHA = 0.16;
const WATER_BLUE = '#3d6f8e';
const WHITE = '#ffffff';

function parseHex(hex: string): [number, number, number] {
  const clean = hex.replace('#', '');
  const full =
    clean.length === 3
      ? clean
          .split('')
          .map((c) => c + c)
          .join('')
      : clean;
  const value = Number.parseInt(full, 16);
  if (full.length !== 6 || !Number.isFinite(value)) return [128, 128, 128];
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

function mix(a: string, b: string, t: number): string {
  const [ar, ag, ab] = parseHex(a);
  const [br, bg, bb] = parseHex(b);
  const blend = (x: number, y: number) => Math.round(x + (y - x) * t);
  return `rgb(${blend(ar, br)}, ${blend(ag, bg)}, ${blend(ab, bb)})`;
}

export function paletteFor(theme: MapTheme): CityPalette {
  return {
    ground: mix(theme.paper, theme.ink, 0.08),
    road: mix(theme.paper, WHITE, 0.66),
    roadCasing: mix(theme.paperEdge, theme.ink, 0.14),
    alley: mix(theme.paper, WHITE, 0.42),
    plaza: mix(theme.paper, WHITE, 0.74),
    water: mix(theme.paper, WATER_BLUE, 0.5),
    waterSheen: mix(theme.paper, WATER_BLUE, 0.28),
    wall: mix(theme.paperEdge, theme.ink, 0.34),
    shadow: theme.ink,
    buildingFill: mix(theme.paper, theme.ink, 0.24),
    buildingRoof: mix(theme.paper, theme.ink, 0.11),
    buildingStroke: mix(theme.ink, theme.paper, 0.34),
  };
}

function strokePolyline(
  ctx: CanvasRenderingContext2D,
  points: readonly Point[],
  projection: BackgroundProjection,
): void {
  ctx.beginPath();
  points.forEach((point, index) => {
    const p = projection.toCanvas(point);
    if (index === 0) ctx.moveTo(p.x, p.y);
    else ctx.lineTo(p.x, p.y);
  });
  ctx.stroke();
}

function strokeSegments(
  ctx: CanvasRenderingContext2D,
  segments: readonly RoadSegment[],
  projection: BackgroundProjection,
  extraPixels: number,
): void {
  for (const segment of segments) {
    const a = projection.toCanvas(segment.a);
    const b = projection.toCanvas(segment.b);
    ctx.lineWidth = Math.max(1, segment.width * projection.unitScale + extraPixels);
    ctx.beginPath();
    ctx.moveTo(a.x, a.y);
    ctx.lineTo(b.x, b.y);
    ctx.stroke();
  }
}

function paintGround(
  ctx: CanvasRenderingContext2D,
  plan: CityPlan,
  projection: BackgroundProjection,
  palette: CityPalette,
): void {
  const topLeft = projection.toCanvas({ x: plan.scene.minX, y: plan.scene.minY });
  const bottomRight = projection.toCanvas({ x: plan.scene.maxX, y: plan.scene.maxY });
  ctx.fillStyle = palette.ground;
  ctx.fillRect(topLeft.x, topLeft.y, bottomRight.x - topLeft.x, bottomRight.y - topLeft.y);
}

function paintCanal(
  ctx: CanvasRenderingContext2D,
  plan: CityPlan,
  projection: BackgroundProjection,
  palette: CityPalette,
): void {
  const width = plan.canal.width * projection.unitScale;
  if (plan.canal.path.length < 2 || width <= 0) return;

  ctx.strokeStyle = palette.wall;
  ctx.lineWidth = width + Math.max(3, width * 0.16);
  strokePolyline(ctx, plan.canal.path, projection);

  ctx.strokeStyle = palette.water;
  ctx.lineWidth = width;
  strokePolyline(ctx, plan.canal.path, projection);

  ctx.strokeStyle = palette.waterSheen;
  ctx.lineWidth = Math.max(1, width * 0.16);
  strokePolyline(ctx, plan.canal.path, projection);
}

function paintStreets(
  ctx: CanvasRenderingContext2D,
  plan: CityPlan,
  projection: BackgroundProjection,
  palette: CityPalette,
): void {
  ctx.strokeStyle = palette.roadCasing;
  strokeSegments(ctx, plan.alleys, projection, 2);
  ctx.strokeStyle = palette.alley;
  strokeSegments(ctx, plan.alleys, projection, 0);

  ctx.strokeStyle = palette.roadCasing;
  strokeSegments(ctx, plan.roads, projection, 3);
  ctx.strokeStyle = palette.road;
  strokeSegments(ctx, plan.roads, projection, 0);
}

function paintPlazas(
  ctx: CanvasRenderingContext2D,
  plan: CityPlan,
  projection: BackgroundProjection,
  palette: CityPalette,
): void {
  for (const plaza of plan.plazas) {
    const center = projection.toCanvas(plaza.center);
    const radius = plaza.radius * projection.unitScale;

    ctx.fillStyle = palette.roadCasing;
    ctx.beginPath();
    ctx.arc(center.x, center.y, radius + 2, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = palette.plaza;
    ctx.beginPath();
    ctx.arc(center.x, center.y, radius, 0, Math.PI * 2);
    ctx.fill();
  }
}

export function footprintRect(
  foot: Point,
  width: number,
  height: number,
): [number, number, number, number] {
  return [foot.x - width / 2, foot.y - height, width, height];
}

function paintFallbackBlock(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  palette: CityPalette,
): void {
  const bodyHeight = height * 0.72;
  const roofHeight = height - bodyHeight;

  ctx.fillStyle = palette.buildingFill;
  ctx.strokeStyle = palette.buildingStroke;
  ctx.lineWidth = 1;

  ctx.beginPath();
  ctx.rect(x - width / 2, y - bodyHeight, width, bodyHeight);
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle = palette.buildingRoof;
  ctx.beginPath();
  ctx.moveTo(x - width / 2 - width * 0.08, y - bodyHeight);
  ctx.lineTo(x, y - bodyHeight - roofHeight);
  ctx.lineTo(x + width / 2 + width * 0.08, y - bodyHeight);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
}

function paintBuilding(
  ctx: CanvasRenderingContext2D,
  building: PlacedBuilding,
  projection: BackgroundProjection,
  palette: CityPalette,
): void {
  const foot = projection.toCanvas(building.anchor);
  const width = building.width * projection.unitScale;
  const height = building.height * projection.unitScale;
  if (width < 1 || height < 1) return;

  ctx.globalAlpha = SHADOW_ALPHA;
  ctx.fillStyle = palette.shadow;
  ctx.beginPath();
  ctx.ellipse(foot.x, foot.y, width * 0.46, width * 0.17, 0, 0, Math.PI * 2);
  ctx.fill();

  ctx.globalAlpha = BUILDING_ALPHA;
  const image = building.spriteId ? spriteImage(building.spriteId) : null;
  if (image) ctx.drawImage(image, ...footprintRect(foot, width, height));
  else paintFallbackBlock(ctx, foot.x, foot.y, width, height, palette);
}

export function paintCity(
  ctx: CanvasRenderingContext2D,
  plan: CityPlan,
  theme: MapTheme,
  projection: BackgroundProjection,
): void {
  const palette = paletteFor(theme);

  ctx.save();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  paintGround(ctx, plan, projection, palette);
  paintCanal(ctx, plan, projection, palette);
  paintStreets(ctx, plan, projection, palette);
  paintPlazas(ctx, plan, projection, palette);

  for (const building of plan.buildings) paintBuilding(ctx, building, projection, palette);

  ctx.restore();
}

export function bakeCityLayer(
  plan: CityPlan,
  theme: MapTheme,
  projection: BackgroundProjection,
  width: number,
  height: number,
  pixelRatio: number,
): HTMLCanvasElement | null {
  if (typeof document === 'undefined') return null;
  if (!(width > 0) || !(height > 0)) return null;
  if (currentLook() === 'diorama') {
    return plan.board
      ? bakeBoardLayer(plan, projection, width, height, pixelRatio)
      : bakeDioramaLayer(plan, projection, width, height, pixelRatio);
  }

  const layer = document.createElement('canvas');
  layer.width = Math.max(1, Math.round(width * pixelRatio));
  layer.height = Math.max(1, Math.round(height * pixelRatio));

  const ctx = layer.getContext('2d');
  if (!ctx) return null;

  ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
  paintCity(ctx, plan, theme, projection);
  return layer;
}
